"use client";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CacheKeys } from "../../constants/CacheKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import { useGetImageFields } from "../../hooks/useImages";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Image } from "../../models/Image";
import { mergeImagesFieldsWithAssociatedActivitiesFields } from "../../services/ImageService";
import { getImageColumnsFromColumnOrder } from "../../services/ViewService";
import { PaginatedList } from "../list/PaginatedList";

const ArchivedActivityImagesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetImageFields();
  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const handleEdit = useCallback(
    (imageId: string) => {
      navigate(`${ClientRoutes.MediaActivityEdit}/overview/${imageId}`);
    },
    [navigate]
  );

  const columns = useMemo(() => {
    if (
      viewDefinitionLoading ||
      !viewDefinition ||
      !activityViewDefinition ||
      activityViewDefinitionLoading
    ) {
      return [];
    }

    return getImageColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      activityViewDefinition.fields,
      viewDefinition.publishedFieldOrder ?? [],
      handleEdit,
      true,
      false
    );
  }, [
    viewDefinition,
    viewDefinitionLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
    handleEdit,
  ]);

  if (viewDefinitionLoading || activityViewDefinitionLoading) {
    return null;
  }

  return (
    <PaginatedList<Image>
      apiEndpoint={ApiEndpoints.ImageCurrentArchived}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      columns={columns}
      searchPlaceholder={t("searchImage")}
      postQueryTransformation={mergeImagesFieldsWithAssociatedActivitiesFields}
      cacheKey={CacheKeys.Image.ImageArchivedList}
      filtersEndpoint={ApiEndpoints.ImageFilters}
      storageKey="images:activity:archived"
      libraryFilterScope={{ listType: "image" }}
    />
  );
};

export default ArchivedActivityImagesList;
